/**
 * Notification Route Resolver
 * Maps notification type + data to the most relevant page in the app.
 *
 * Priority:
 * 1. data.deepLink — backend can override any destination (must be internal)
 * 2. Type-specific logic based on notification type + available data IDs
 *    (each type has its own fallback, e.g. "request" → /requests, others → /projects)
 * 3. Global fallback to /projects list
 */

import type { NotificationData, NotificationType } from "@shared/types";

export interface NotificationRoute {
	path: string;
	/** Whether this notification should be clickable */
	clickable: boolean;
}

/**
 * Resolve the best route for a notification.
 * Accepts both known NotificationType values and unknown/seed types.
 */
export function getNotificationRoute(
	type: NotificationType | string,
	data?: NotificationData | null,
): NotificationRoute {
	// 1. Backend deep-link override (only allow internal paths)
	if (data?.deepLink) {
		const link = data.deepLink;
		if (link.startsWith("/") && !link.startsWith("//")) {
			return { path: link, clickable: true };
		}
		// Reject external / protocol-relative URLs — fall through to type logic
	}

	const projectId = data?.projectId;
	const invitationId = data?.invitationId;
	const requestId = data?.requestId;

	switch (type) {
		// ── Request notifications ──────────────────────────────
		case "request": {
			if (invitationId) {
				return { path: `/invitations/${invitationId}`, clickable: true };
			}
			if (requestId) {
				return { path: "/requests", clickable: true };
			}
			if (projectId) {
				return { path: `/projects/${projectId}`, clickable: true };
			}
			return { path: "/requests", clickable: true };
		}

		// ── Proposal notifications ─────────────────────────────
		case "proposal":
		case "proposal_received": {
			if (projectId) {
				return {
					path: `/projects/${projectId}?tab=team`,
					clickable: true,
				};
			}
			return { path: "/projects", clickable: true };
		}

		case "proposal_accepted": {
			if (projectId) {
				return { path: `/projects/${projectId}`, clickable: true };
			}
			return { path: "/projects", clickable: true };
		}

		case "proposal_rejected": {
			return { path: "/requests", clickable: true };
		}

		case "project_published": {
			if (projectId) {
				return { path: `/public-projects/${projectId}`, clickable: true };
			}
			return { path: "/opportunities", clickable: true };
		}

		// ── Project lifecycle ──────────────────────────────────
		case "project_awaiting_funding": {
			if (projectId) {
				return {
					path: `/projects/${projectId}?tab=payments`,
					clickable: true,
				};
			}
			return { path: "/projects", clickable: true };
		}

		case "project_funded": {
			if (projectId) {
				return { path: `/projects/${projectId}`, clickable: true };
			}
			return { path: "/projects", clickable: true };
		}

		case "project_completed":
		case "project_cancelled": {
			if (projectId) {
				return { path: `/projects/${projectId}`, clickable: true };
			}
			return { path: "/projects", clickable: true };
		}

		// ── Payment / milestone ────────────────────────────────
		case "payment_released":
		case "dispute_resolved":
		case "payment_reminder": {
			if (projectId) {
				return {
					path: `/projects/${projectId}?tab=milestones`,
					clickable: true,
				};
			}
			return { path: "/projects", clickable: true };
		}

		case "milestone_completed": {
			if (projectId) {
				return {
					path: `/projects/${projectId}?tab=milestones`,
					clickable: true,
				};
			}
			return { path: "/projects", clickable: true };
		}

		// ── Budget alerts ──────────────────────────────────────
		case "budget_warning":
		case "budget_exceeded": {
			if (projectId) {
				return { path: `/projects/${projectId}?tab=boq`, clickable: true };
			}
			return { path: "/projects", clickable: true };
		}

		// ── BOQ events ─────────────────────────────────────────
		// The backend sends a deepLink with each of these; this is the fallback.
		// The homeowner reviews a BOQ on the project's BOQ tab; its author edits
		// it, and answers change requests, on the BOQ's own page.
		case "boq_sent":
		case "boq_change_request_resolved": {
			if (projectId) {
				return { path: `/projects/${projectId}?tab=boq`, clickable: true };
			}
			return { path: "/projects", clickable: true };
		}
		case "boq_lines_approved":
		case "boq_approved":
		case "boq_changes_requested": {
			if (data?.boqId) {
				return { path: `/boqs/${data.boqId}`, clickable: true };
			}
			if (projectId) {
				return { path: `/projects/${projectId}?tab=boq`, clickable: true };
			}
			return { path: "/projects", clickable: true };
		}

		// ── Task updates ───────────────────────────────────────
		case "task_update": {
			if (projectId) {
				return { path: `/projects/${projectId}?tab=tasks`, clickable: true };
			}
			return { path: "/projects", clickable: true };
		}

		// ── Messages / comments ────────────────────────────────
		case "message": {
			if (projectId) {
				return {
					path: `/projects/${projectId}?tab=messages`,
					clickable: true,
				};
			}
			return { path: "/projects", clickable: true };
		}

		// ── Realtor WhatsApp connection ────────────────────────
		// The backend sends a deepLink with each of these; this is the fallback.
		// The connection's card is on realtor settings.
		case "whatsapp_disconnected":
		case "whatsapp_broken": {
			return { path: "/realtor/settings", clickable: true };
		}

		// ── Unknown / future types ─────────────────────────────
		default: {
			if (projectId) {
				return { path: `/projects/${projectId}`, clickable: true };
			}
			// Seed or unknown notifications without projectId → projects list
			return { path: "/projects", clickable: true };
		}
	}
}
